import { useCallback, useLayoutEffect, useState } from 'react';
import { CommentsTypes, formDataSubCommentTypes } from '../types';
import { API, catchFunction } from '../utils';
import { UseSubConmmentsTypes } from './hooks.types';

export const useSubComments = ({
    open,
    url,
    user,
    setFieldValue,
    setCurrentComment,
    deleteCommentUrl,
    likeCommentUrl,
}: UseSubConmmentsTypes) => {
    const [cachedsubComments, setCachedsubComments] = useState<CommentsTypes[] | false>(false);
    const [subComments, setSubComments] = useState<CommentsTypes[] | false>(cachedsubComments || []);
    const [subCommentLoading, setSubCommentLoading] = useState(false);
    const [actionsLoading, setActionsLoading] = useState<number>(0);
    const [, setPostCommentLoading] = useState(false);
    const [editId, setIdEdit] = useState<number>(0);
    const [indexEdit, setIndexEdit] = useState(0);
    const getComments = useCallback(
        async (signal: AbortSignal) => {
            try {
                setSubCommentLoading(true);
                const { data } = await API.get(url, { params: signal });
                setSubComments(data);
                setCachedsubComments(data);
            } catch (error) {
                catchFunction(error);
            } finally {
                setSubCommentLoading(false);
            }
        },
        [url],
    );
    useLayoutEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        open && getComments(signal);
        return () => controller.abort();
    }, [getComments, open]);
    const postSubComment = async (postUrl: string, values: formDataSubCommentTypes) => {
        try {
            setPostCommentLoading(true);
            const { data } = await API.post(postUrl, values);
            if (!subComments) return;
            setSubComments([
                {
                    ...data,
                    createdBy: { id: user?.id, fullName: user?.fullName },
                },
                ...subComments,
            ]);
            setFieldValue('content', '');
            setCurrentComment(({ commentCount, ...prev }) => ({
                ...prev,
                commentCount: (+commentCount + 1).toString(),
            }));
        } catch (error) {
            catchFunction(error);
        } finally {
            setPostCommentLoading(false);
        }
    };
    const deleteComment = async (id: number) => {
        try {
            setActionsLoading(id);
            await API.delete(`${deleteCommentUrl}/${id}`);
            subComments && setSubComments(subComments?.filter((comment) => comment?.id !== id));
            setCurrentComment(({ commentCount, ...prev }) => ({
                ...prev,
                commentCount: (+commentCount - 1).toString(),
            }));
        } catch (error) {
            catchFunction(error);
        } finally {
            setActionsLoading(0);
        }
    };
    const editComment = async (subComment: CommentsTypes, index: number) => {
        setFieldValue('editContent', subComment?.content);
        setIdEdit(subComment?.id);
        setIndexEdit(index);
    };
    const updateSubComment = async (updateCommentUrl: string, values: { content: string; userId: string }) => {
        try {
            setPostCommentLoading(true);
            await API.patch(updateCommentUrl, values);
            const copy = subComments;
            if (copy) {
                copy[indexEdit].content = values?.content;
                setSubComments(copy);
            }
            setIdEdit(0);
            setFieldValue('editContent', '');
            setIndexEdit(0);
        } catch (error) {
            catchFunction(error);
        } finally {
            setPostCommentLoading(false);
        }
    };
    const handleLikeSubComment = async (index: number, repli: CommentsTypes) => {
        try {
            await API.post(`${likeCommentUrl.p1}/${repli?.id}/${likeCommentUrl.p2}`);
            const copy = subComments;
            if (copy) {
                copy[index].isLiked = !copy[index].isLiked;
                copy[index].reactionCount = copy[index].isLiked
                    ? (+copy[index].reactionCount - 1).toString()
                    : (+copy[index].reactionCount + 1).toString();
            }
        } catch (error) {
            catchFunction(error);
        }
    };
    return {
        subComments,
        subCommentLoading,
        updateSubComment,
        deleteComment,
        postSubComment,
        editId,
        actionsLoading,
        editComment,
        handleLikeSubComment,
    };
};
